import { Link } from "react-router-dom";
import {
  HelpCircle, ArrowRight, Image as ImageIcon, FileText, Menu as MenuIcon,
  Newspaper, Award, Briefcase, Users, Sparkles, LayoutGrid, Compass,
  MousePointerClick, RefreshCw, ChevronRight, ExternalLink,
} from "lucide-react";

// Small section-header helper for consistency.
function Section({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-xl overflow-hidden"
    >
      <div className="flex items-start gap-3 p-5 border-b border-slate-100 bg-slate-50/50">
        <div className="w-10 h-10 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-sky-600" />
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="p-5 space-y-3 text-sm text-slate-700 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex gap-3">
      <span className="w-6 h-6 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
        {n}
      </span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-block font-mono text-[11px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">
      {children}
    </span>
  );
}

function GoTo({ to, label }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 font-semibold"
    >
      {label} <ChevronRight className="h-3.5 w-3.5" />
    </Link>
  );
}

export default function AdminTutorialPage() {
  return (
    <div data-testid="admin-tutorial-page" className="min-h-screen">
      <header className="bg-white border-b border-slate-200 px-5 lg:px-8 py-6 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center">
            <HelpCircle className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Admin Tutorial</h1>
            <p className="text-xs text-slate-500">
              Everything you can edit on your Antera website, and where to do it. Bookmark this page.
            </p>
          </div>
        </div>
      </header>

      <div className="p-5 lg:p-8 max-w-5xl mx-auto space-y-6">
        {/* Quick reference */}
        <Section
          icon={LayoutGrid}
          title="Quick reference — What can I edit?"
          subtitle="Everything below is self-service. No developer or redeploy required."
          testId="tutorial-quick-ref"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left border-b border-slate-200">
                  <th className="py-2 pr-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">I want to…</th>
                  <th className="py-2 pr-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Where</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 pr-3">{"Edit an existing page's headlines, blocks, images, sections"}</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/site-pages" label="Site Pages" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Add / remove / rename / reorder a top-nav menu item</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/navigation" label="Top Navigation" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Create a new page (quick, URL is <Pill>/p/slug</Pill>)</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/pages" label="Custom Pages" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Publish a new blog post</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/blog" label="Blog Posts" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Add or edit a case study</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/case-studies" label="Case Studies" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Open a new job posting</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/jobs" label="Jobs / Careers" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Add or edit team / leadership members</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/team" label="Team" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Add or edit product features</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/features" label="Features" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Change footer, social links, contact info, SEO defaults</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/site-settings" label="Site Settings" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Upload or browse images</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/media" label="Media Library" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">See all available block types with previews</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/block-library" label="Block Library" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Check what visitors are searching for</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/search-analytics" label="Search Analytics" /></td>
                </tr>
                <tr>
                  <td className="py-2 pr-3">Review contact form submissions</td>
                  <td className="py-2 pr-3"><GoTo to="/admin/leads" label="Leads Inbox" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* Edit existing page */}
        <Section
          icon={FileText}
          title="How to edit an existing page"
          subtitle="Homepage, Products, Solutions, Advance, Aether, Plans, and every other page in your site."
          testId="tutorial-edit-page"
        >
          <Step n={1}>
            Go to <GoTo to="/admin/site-pages" label="Site Pages" /> in the left sidebar.
          </Step>
          <Step n={2}>
            Click the page you want to edit. Every page is made of <strong>blocks</strong> — hero,
            cards, images, CTAs, testimonials, etc.
          </Step>
          <Step n={3}>
            Inside the editor you can:
            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-600">
              <li>{"Edit any block's text, images, links directly in the form"}</li>
              <li>Add a new block from the <strong>{'"+ Add block"'}</strong> menu at the bottom</li>
              <li>Reorder blocks with the up/down arrows</li>
              <li>Remove a block with the trash icon</li>
              <li>Hide a page from the public site by turning off <strong>Published</strong></li>
            </ul>
          </Step>
          <Step n={4}>
            Click <strong>Save</strong>. Changes go live within a few seconds. No redeploy needed.
          </Step>
          <div className="mt-4 p-3 bg-emerald-50/60 border border-emerald-100 rounded-lg text-xs text-emerald-900">
            <strong>Repeatable lists inside a block</strong> (Elite Suppliers logos, feature cards, testimonials, navigation dropdowns, industry cards, benefit rows, etc.) — each row has its own <Pill>Move up</Pill> / <Pill>Move down</Pill> / <Pill>Remove</Pill> buttons, and a big <Pill>+ Add</Pill> button at the bottom. Example: to add a new supplier logo on the homepage, open <GoTo to="/admin/site-pages/home" label="Homepage" /> → expand <em>Block #2 — Elite Suppliers Logo Marquee</em> → scroll to <em>Supplier logos</em> → click <strong>+ Add</strong> → enter the supplier name and pick a logo image → Save.
          </div>
          <div className="mt-2 p-3 bg-amber-50/60 border border-amber-100 rounded-lg text-xs text-amber-900">
            <strong>Preview before publishing:</strong> Use the <strong>Draft & Preview</strong> panel
            inside any page editor to make edits in a private draft, share a preview link, and only
            {"promote to live once you're happy."}
          </div>
        </Section>

        {/* Top navigation editor - NEW */}
        <Section
          icon={Compass}
          title="How to edit the top navigation (menu)"
          subtitle="Add, remove, rename, or reorder menu items — including sub-items in each dropdown."
          testId="tutorial-navigation"
        >
          <Step n={1}>
            Go to <GoTo to="/admin/navigation" label="Top Navigation" /> in the left sidebar.
          </Step>
          <Step n={2}>
            Each row on that page is one top-level menu button (Solutions, Products, Exchange, Why
            Antera, Insights, Careers, etc.). Fill in three fields per row:
            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-600">
              <li><strong>Label</strong> — the visible text (e.g. <Pill>Products</Pill>)</li>
              <li><strong>Link (URL path)</strong> — where clicking takes visitors (e.g. <Pill>/products</Pill>)</li>
              <li><strong>Icon</strong> — used on the mobile menu; pick from the dropdown</li>
            </ul>
          </Step>
          <Step n={3}>
            To turn a row into a <strong>dropdown</strong>, click{" "}
            <Pill>+ Add sub-item</Pill> {"and enter each sub-item's label + link."}
            A row with zero sub-items becomes a simple link (no dropdown).
          </Step>
          <Step n={4}>
            Use the up/down chevrons to reorder rows or sub-items. Use the trash icon to remove.
          </Step>
          <Step n={5}>
            Click <strong>Save changes</strong>. The menu updates on the live site immediately —
            desktop and mobile.
          </Step>
          <div className="mt-2 p-3 bg-sky-50/60 border border-sky-100 rounded-lg text-xs text-sky-900">
            <strong>Common workflow — add a new page to the menu:</strong>
            <ol className="list-decimal list-inside mt-1 space-y-0.5">
              <li>First create the page in <GoTo to="/admin/pages" label="Custom Pages" /> or via Site Pages.</li>
              <li>{"Come back here and add a menu row pointing to that page's URL."}</li>
            </ol>
          </div>
        </Section>

        {/* Creating a new page */}
        <Section
          icon={FileText}
          title="How to create a new page"
          subtitle="Two ways, depending on how polished the URL needs to be."
          testId="tutorial-new-page"
        >
          <div className="p-4 bg-emerald-50/60 border border-emerald-100 rounded-lg">
            <p className="font-semibold text-emerald-900 mb-2">
              Option A — Custom Page (quick, self-service, 5 min)
            </p>
            <ol className="list-decimal list-inside space-y-1 text-emerald-900/80 text-xs">
              <li>Go to <GoTo to="/admin/pages" label="Custom Pages" /></li>
              <li>Click <strong>{'"New Page"'}</strong></li>
              <li>Fill in title, slug, and body content (rich text with images, headings, lists, links)</li>
              <li>Save. Live at <Pill>anterasoftware.com/p/your-slug</Pill></li>
              <li>Optional: Add it to the menu in <GoTo to="/admin/navigation" label="Top Navigation" /></li>
            </ol>
            <p className="text-xs text-emerald-900/70 mt-2">
              URL always starts with <Pill>/p/…</Pill>. Perfect for one-off landing pages, partner pages, promotions.
            </p>
          </div>
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="font-semibold text-slate-800 mb-2">
              Option B — Full site page with clean URL (needs a developer)
            </p>
            <p className="text-xs text-slate-600 leading-relaxed">
              For pages you want at a clean URL like <Pill>/plans</Pill> or <Pill>/about-us</Pill>{" "}
              instead of <Pill>/p/plans</Pill>. Send Ed a note like:
            </p>
            <p className="text-xs text-slate-600 italic mt-2 pl-3 border-l-2 border-slate-300">
              {'"Please add a new page called '}<strong>[Name]</strong> at URL{" "}
              <Pill>/[url-slug]</Pill>{'."'}
            </p>
            <p className="text-xs text-slate-600 mt-2">
              Ed will register the URL (~10 minutes + redeploy). After that,{" "}
              <strong>you edit all the page content yourself</strong> from <Pill>/admin/site-pages/[slug]</Pill>{" "}
              like any other page.
            </p>
          </div>
        </Section>

        {/* Uploading images */}
        <Section
          icon={ImageIcon}
          title="How to upload and use images"
          subtitle="All images live in one library. Reuse them anywhere."
          testId="tutorial-images"
        >
          <Step n={1}>
            Go to <GoTo to="/admin/media" label="Media Library" />
          </Step>
          <Step n={2}>
            Click <strong>Upload</strong> and pick a file. PNG, JPG, WEBP, and GIF are supported.
          </Step>
          <Step n={3}>
            Once uploaded, the image is available in every image picker across the admin — hero
            blocks, blog featured images, team photos, case study heroes, etc.
          </Step>
          <div className="mt-2 p-3 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-600">
            <strong>Shortcut:</strong> You can also upload directly from any block editor. Look
            for the <Pill>Change image</Pill> button on image fields.
          </div>
        </Section>

        {/* Blog, case studies, jobs, features, team */}
        <Section
          icon={Newspaper}
          title="Blog, Case Studies, Jobs, Features, Team"
          subtitle="Each has its own dedicated editor. Same pattern for all of them."
          testId="tutorial-collections"
        >
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <Newspaper className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" />
              <span><GoTo to="/admin/blog" label="Blog Posts" /> — publish articles that appear on <Pill>/blog</Pill></span>
            </li>
            <li className="flex items-start gap-2">
              <Award className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <span><GoTo to="/admin/case-studies" label="Case Studies" /> — publish customer stories with full long-form content (subtitle, quote, challenge, solution, results, hero image)</span>
            </li>
            <li className="flex items-start gap-2">
              <Briefcase className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
              <span><GoTo to="/admin/jobs" label="Jobs / Careers" /> — open positions shown on <Pill>/careers</Pill></span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
              <span><GoTo to="/admin/features" label="Features" /> — product feature pages linked from Advance, Aether, Features grid</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
              <span><GoTo to="/admin/team" label="Team" /> — leadership photos and bios shown on <Pill>/why</Pill></span>
            </li>
          </ul>
          <p className="text-xs text-slate-500 mt-3">
            Each editor supports the same actions: <strong>list</strong>, <strong>edit</strong>,{" "}
            <strong>publish / hide</strong>, <strong>reorder</strong>, <strong>duplicate</strong>,{" "}
            <strong>delete</strong>.
          </p>
        </Section>

        {/* Features page walkthrough (added 2026-08-11 answering client Q) */}
        <Section
          icon={Sparkles}
          title={'How to add cards & sections on the /features page'}
          subtitle="The Features page is a grid of feature cards grouped into sections like 'Sales & CRM' and 'Orders & Production'. Everything is editable."
          testId="tutorial-features-page"
        >
          <div className="p-3 bg-sky-50/60 border border-sky-100 rounded-lg text-xs text-sky-900">
            <strong>Terminology reminder:</strong>
            <ul className="mt-1 space-y-0.5 list-disc list-inside">
              <li>A <strong>section</strong> (like {'"Sales & CRM"'} or {'"Orders & Production"'}) is a <em>group</em></li>
              <li>A <strong>card</strong> (like Presentations, Sourcing, CRM) is a <em>feature</em></li>
            </ul>
          </div>

          <p className="font-semibold text-slate-800 mt-2">To add a new SECTION (group):</p>
          <Step n={1}>
            Go to <GoTo to="/admin/features" label="Features" />
          </Step>
          <Step n={2}>
            Click <strong>{'"Manage groups"'}</strong> at the top-right of the page.
          </Step>
          <Step n={3}>
            Add a new group. Fill in:
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-slate-600">
              <li><strong>Title</strong> — the section heading (e.g. <Pill>Reporting & Analytics</Pill>)</li>
              <li><strong>Slug</strong> — the URL-safe name (e.g. <Pill>reporting</Pill>)</li>
              <li><strong>Description</strong> — the small subtitle shown under the heading</li>
              <li><strong>Order</strong> — lower numbers appear first (10, 20, 30…)</li>
            </ul>
          </Step>
          <Step n={4}>
            Save. The new section appears on <Pill>/features</Pill> immediately, empty. Now add cards to it.
          </Step>

          <p className="font-semibold text-slate-800 mt-4">To add a new CARD (feature):</p>
          <Step n={1}>
            From <GoTo to="/admin/features" label="Features" />, click <strong>{'"New feature"'}</strong> at the top-right.
          </Step>
          <Step n={2}>
            Fill in:
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-slate-600">
              <li><strong>Name</strong> — the card headline (e.g. <Pill>AI Reporting</Pill>)</li>
              <li><strong>Slug</strong> — URL segment, only needed if this card has a detail page</li>
              <li><strong>Description</strong> — the two-line summary under the name</li>
              <li><strong>Icon</strong> — pick from the dropdown of Lucide icons</li>
              <li><strong>Group</strong> — pick which section this card sits under</li>
              <li><strong>Order</strong> — position within the group (10, 20, 30…)</li>
              <li><strong>Has detail page</strong> — turn on if this card should be clickable to a longer page at <Pill>/features/&lt;slug&gt;</Pill></li>
              <li><strong>Published</strong> — turn on to make it visible</li>
            </ul>
          </Step>
          <Step n={3}>
            Save. The card appears in the grid on <Pill>/features</Pill> instantly.
          </Step>

          <p className="font-semibold text-slate-800 mt-4">To reorder, edit, hide, or delete:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-600 text-xs">
            <li>Use the <strong>up/down arrows</strong> in the <Pill>ORDER</Pill> column to reorder</li>
            <li>Click the <strong>pencil</strong> icon on any row to edit</li>
            <li>Click the <strong>eye</strong> icon to hide (Draft) without deleting</li>
            <li>Click the <strong>trash</strong> icon to permanently delete</li>
          </ul>
        </Section>

        {/* Site settings */}
        <Section
          icon={MenuIcon}
          title="Site-wide settings — footer, social, contact, SEO"
          subtitle="Things that appear on every page."
          testId="tutorial-site-settings"
        >
          <p>
            Open <GoTo to="/admin/site-settings" label="Site Settings" />. The tabs there let you edit:
          </p>
          <ul className="list-disc list-inside space-y-1 text-slate-600">
            <li><strong>Social & Contact</strong> — footer social links (LinkedIn, YouTube, X, Instagram, Facebook), sales email/phone/address</li>
            <li><strong>Suppliers</strong> — the Elite Supplier logo carousel on the homepage</li>
            <li><strong>Testimonials</strong> — rotating quotes on the homepage</li>
            <li>Homepage content itself is edited from <GoTo to="/admin/site-pages/home" label="Site Pages → home" />, NOT here</li>
          </ul>
        </Section>

        {/* Leads / applications */}
        <Section
          icon={Briefcase}
          title="Reviewing leads and job applications"
          subtitle="Every form submission on the site is stored and viewable in the admin."
          testId="tutorial-leads"
        >
          <ul className="space-y-2">
            <li><GoTo to="/admin/leads" label="Leads Inbox" /> {'— every "Request a Demo" and Contact form submission. Mark as new / read / responded / archived. Export to CSV.'}</li>
            <li><GoTo to="/admin/applications" label="Applications" /> — every job applicant with attached resume.</li>
          </ul>
        </Section>

        {/* Publishing / preview */}
        <Section
          icon={RefreshCw}
          title="Do I need to redeploy for my changes to appear?"
          subtitle="Almost never. Here's the honest breakdown."
          testId="tutorial-publish"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left border-b border-slate-200">
                  <th className="py-2 pr-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Change</th>
                  <th className="py-2 pr-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Redeploy needed?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr><td className="py-2 pr-3">Any content edit (text, images, blocks, blog, case studies, jobs, team, features)</td><td className="py-2 pr-3 text-emerald-700 font-semibold">No — instant</td></tr>
                <tr><td className="py-2 pr-3">Adding / removing / reordering menu items</td><td className="py-2 pr-3 text-emerald-700 font-semibold">No — instant</td></tr>
                <tr><td className="py-2 pr-3">Adding a Custom Page at <Pill>/p/slug</Pill></td><td className="py-2 pr-3 text-emerald-700 font-semibold">No — instant</td></tr>
                <tr><td className="py-2 pr-3">Uploading a new image</td><td className="py-2 pr-3 text-emerald-700 font-semibold">No — instant</td></tr>
                <tr><td className="py-2 pr-3">Adding a full site page with a clean URL (e.g. <Pill>/plans</Pill>)</td><td className="py-2 pr-3 text-amber-700 font-semibold">Yes — ask Ed</td></tr>
                <tr><td className="py-2 pr-3">Building a brand-new type of block or layout</td><td className="py-2 pr-3 text-amber-700 font-semibold">Yes — ask Ed</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* Stuck? */}
        <Section
          icon={MousePointerClick}
          title="Stuck? Can't find something?"
          subtitle="Two quick ways to get unblocked."
          testId="tutorial-stuck"
        >
          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong>Check the Block Library.</strong> <GoTo to="/admin/block-library" label="Block Library" />{" "}
              shows every block type with a live preview and every field it supports.
            </li>
            <li>
              <strong>Send Ed a screenshot.</strong> Include the page URL and circle the element
              {" you want to change. Ed will point you to the right editor or make the change if it's "}
              code-side.
            </li>
          </ol>
          <div className="mt-3 flex gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-sky-600"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open live site
            </a>
            <Link
              to="/admin/site-pages"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700"
            >
              Start editing pages <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Section>
      </div>
    </div>
  );
}
